import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { DealPreviewData } from "@/features/videoProduction/stores/videoProductionFunctionalSlice";
import type {
  DealPayment,
  PaymentEntry,
} from "@/features/leads/components/view-lead/components/Ongoing/mockData";

type DocumentType = "invoice" | "quotation";

interface TenantInfo {
  tenantName: string;
  tenantLogoUrl?: string;
  tenantAddress?: string;
  gstin?: string;
  paymentInfo?: string;
}

interface DocumentOptions {
  type: DocumentType;
  previewData: DealPreviewData;
  tenant: TenantInfo;
  clientName: string;
  documentNumber: string;
}

const fmt = (v: number) =>
  v.toLocaleString("en-IN", { maximumFractionDigits: 0 });

function getDocumentTitle(type: DocumentType): string {
  return type === "invoice" ? "INVOICE" : "QUOTATION";
}

function getDocumentNumberLabel(type: DocumentType): string {
  return type === "invoice" ? "Invoice No" : "Quotation No";
}

export function getNextDocumentNumber(
  type: DocumentType,
  tenantId: number | string
): string {
  const key = `xale_${type}_counter_${tenantId}`;
  const current = parseInt(localStorage.getItem(key) || "0", 10);
  const next = current + 1;
  localStorage.setItem(key, String(next));

  const prefix = type === "invoice" ? "INV" : "QTN";
  return `${prefix}-${String(next).padStart(4, "0")}`;
}

export function generateDocumentPdf(options: DocumentOptions) {
  const { type, previewData, tenant, clientName, documentNumber } = options;

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 16;
  let y = 20;

  // --- HEADER: Company info (left) + Doc info (right) ---
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(5, 25, 18);
  doc.text(tenant.tenantName || "Company", margin, y);

  y += 7;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(105, 117, 113);

  if (tenant.tenantAddress) {
    const addressLines = doc.splitTextToSize(
      tenant.tenantAddress,
      pageWidth / 2 - margin
    );
    doc.text(addressLines, margin, y);
    y += addressLines.length * 4;
  }

  if (tenant.gstin) {
    doc.text(`GSTIN: ${tenant.gstin}`, margin, y);
    y += 5;
  }

  // Right side: document type title + number + date
  const rightX = pageWidth - margin;
  let rightY = 20;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(38, 115, 85);
  doc.text(getDocumentTitle(type), rightX, rightY, { align: "right" });

  rightY += 9;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(55, 71, 65);
  doc.text(
    `${getDocumentNumberLabel(type)}: ${documentNumber}`,
    rightX,
    rightY,
    { align: "right" }
  );

  rightY += 5;
  const today = new Date();
  const dateStr = today.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  doc.text(`Date: ${dateStr}`, rightX, rightY, { align: "right" });

  y = Math.max(y, rightY) + 8;

  // --- DIVIDER ---
  doc.setDrawColor(230, 232, 231);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 6;

  // --- CLIENT INFO ---
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(5, 25, 18);
  doc.text("Bill To:", margin, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(55, 71, 65);
  doc.text(clientName || "Client", margin, y);
  y += 5;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(105, 117, 113);
  doc.text(`Service: ${previewData.serviceName}`, margin, y);
  y += 8;

  // --- TABLE ---
  const tableHeaders = ["Sr No", "Item Name", "Unit Price", "Qty", "Total"];
  const tableRows: (string | number)[][] = [];

  let srNo = 1;
  for (const entry of previewData.entries) {
    tableRows.push([
      srNo,
      entry.variantName,
      `Rs. ${fmt(entry.unitPrice)}`,
      entry.videoCount,
      `Rs. ${fmt(entry.subtotal)}`,
    ]);
    srNo++;
  }

  autoTable(doc, {
    startY: y,
    head: [tableHeaders],
    body: tableRows,
    margin: { left: margin, right: margin },
    theme: "grid",
    headStyles: {
      fillColor: [38, 115, 85],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9,
      halign: "center",
    },
    bodyStyles: {
      fontSize: 9,
      textColor: [55, 71, 65],
      halign: "center",
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 16 },
      1: { halign: "left", cellWidth: "auto" },
      2: { halign: "right", cellWidth: 38 },
      3: { halign: "center", cellWidth: 16 },
      4: { halign: "right", cellWidth: 38 },
    },
    alternateRowStyles: {
      fillColor: [247, 249, 248],
    },
  });

  y = (doc as any).lastAutoTable.finalY + 8;

  // --- PAYMENT SUMMARY ---
  const summaryX = pageWidth - margin - 80;
  const summaryValueX = pageWidth - margin;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(105, 117, 113);

  doc.text("Gross Total:", summaryX, y);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(5, 25, 18);
  doc.text(`Rs. ${fmt(previewData.grossTotal)}`, summaryValueX, y, {
    align: "right",
  });
  y += 6;

  if (previewData.discountAmount > 0) {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(105, 117, 113);
    const discountLabel =
      previewData.discountType === "percentage"
        ? `Discount (${previewData.discountValue}%):`
        : "Discount:";
    doc.text(discountLabel, summaryX, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(239, 83, 80);
    doc.text(`-Rs. ${fmt(previewData.discountAmount)}`, summaryValueX, y, {
      align: "right",
    });
    y += 6;
  }

  doc.setDrawColor(230, 232, 231);
  doc.line(summaryX, y, summaryValueX, y);
  y += 5;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(5, 25, 18);
  doc.text("Net Amount:", summaryX, y);
  doc.text(`Rs. ${fmt(previewData.netAmount)}`, summaryValueX, y, {
    align: "right",
  });
  y += 6;

  if (previewData.advance > 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(38, 115, 85);
    doc.text("Advance Collected:", summaryX, y);
    doc.text(`Rs. ${fmt(previewData.advance)}`, summaryValueX, y, {
      align: "right",
    });
    y += 6;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(234, 88, 12);
  doc.text("Pending:", summaryX, y);
  doc.text(`Rs. ${fmt(previewData.pending)}`, summaryValueX, y, {
    align: "right",
  });
  y += 12;

  // --- PAYMENT INFORMATION ---
  if (tenant.paymentInfo) {
    doc.setDrawColor(230, 232, 231);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(5, 25, 18);
    doc.text("Payment Information", margin, y);
    y += 6;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(55, 71, 65);
    const paymentLines = tenant.paymentInfo.split("\n");
    for (const line of paymentLines) {
      doc.text(line, margin, y);
      y += 4.5;
    }
  }

  // --- FOOTER ---
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(155, 163, 160);
  doc.text("powered by xale.in", pageWidth / 2, pageHeight - 10, {
    align: "center",
  });

  const fileName = `${getDocumentTitle(type)}_${documentNumber}_${dateStr.replace(/\s/g, "-")}.pdf`;
  doc.save(fileName);
}

// ---------------------------------------------------------------------------
// Per-deal invoice generation (from Payments tab)
// ---------------------------------------------------------------------------

interface DealInvoiceOptions {
  deal: DealPayment;
  tenant: TenantInfo;
  clientName: string;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function generateDealInvoicePdf(options: DealInvoiceOptions) {
  const { deal, tenant, clientName } = options;
  const tenantId =
    (typeof (tenant as any).id === "number" ? (tenant as any).id : "0") || "0";
  const docNumber = getNextDocumentNumber("invoice", tenantId);

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  const m = 16;
  let y = 20;

  // --- Header ---
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(5, 25, 18);
  doc.text(tenant.tenantName || "Company", m, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(105, 117, 113);
  if (tenant.tenantAddress) {
    const lines = doc.splitTextToSize(tenant.tenantAddress, pw / 2 - m);
    doc.text(lines, m, y);
    y += lines.length * 4;
  }
  if (tenant.gstin) {
    doc.text(`GSTIN: ${tenant.gstin}`, m, y);
    y += 5;
  }

  const rx = pw - m;
  let ry = 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(38, 115, 85);
  doc.text("INVOICE", rx, ry, { align: "right" });
  ry += 9;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(55, 71, 65);
  doc.text(`Invoice No: ${docNumber}`, rx, ry, { align: "right" });
  ry += 5;
  doc.text(`Date: ${fmtDate(new Date().toISOString())}`, rx, ry, {
    align: "right",
  });

  y = Math.max(y, ry) + 8;
  doc.setDrawColor(230, 232, 231);
  doc.setLineWidth(0.5);
  doc.line(m, y, pw - m, y);
  y += 6;

  // --- Client + Deal ---
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(5, 25, 18);
  doc.text("Bill To:", m, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setTextColor(55, 71, 65);
  doc.text(clientName || "Client", m, y);
  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(105, 117, 113);
  doc.text(`Stage: ${deal.stageName}`, m, y);
  y += 8;

  // --- Item Details Table ---
  const itemHeaders = [
    "Sr No",
    "Item / Deal",
    "Rate",
    "Discount",
    "Net Amount",
  ];
  const itemRows: string[][] = [
    [
      "1",
      deal.dealName,
      `Rs. ${fmt(deal.totalValue)}`,
      deal.discountAmount > 0 ? `Rs. ${fmt(deal.discountAmount)}` : "-",
      `Rs. ${fmt(deal.netAmount)}`,
    ],
  ];

  autoTable(doc, {
    startY: y,
    head: [itemHeaders],
    body: itemRows,
    margin: { left: m, right: m },
    theme: "grid",
    headStyles: {
      fillColor: [38, 115, 85],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9.5,
      halign: "center",
    },
    bodyStyles: {
      fontSize: 10,
      textColor: [5, 25, 18],
      fontStyle: "bold",
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 16 },
      1: { halign: "left", cellWidth: "auto" },
      2: { halign: "right", cellWidth: 34 },
      3: { halign: "right", cellWidth: 30 },
      4: { halign: "right", cellWidth: 36 },
    },
    alternateRowStyles: { fillColor: [247, 249, 248] },
  });

  y = (doc as any).lastAutoTable.finalY + 8;

  // --- Financial Summary (right-aligned) ---
  const sX = pw - m - 85;
  const sVX = pw - m;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(105, 117, 113);
  doc.text("Deal Value:", sX, y);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(5, 25, 18);
  doc.text(`Rs. ${fmt(deal.totalValue)}`, sVX, y, { align: "right" });
  y += 6;

  if (deal.discountAmount > 0) {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(105, 117, 113);
    doc.text("Discount:", sX, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(239, 83, 80);
    doc.text(`-Rs. ${fmt(deal.discountAmount)}`, sVX, y, { align: "right" });
    y += 6;
  }

  doc.setDrawColor(230, 232, 231);
  doc.line(sX, y, sVX, y);
  y += 5;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(5, 25, 18);
  doc.text("Net Amount:", sX, y);
  doc.text(`Rs. ${fmt(deal.netAmount)}`, sVX, y, { align: "right" });
  y += 7;

  if (deal.advanceAmount > 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(38, 115, 85);
    doc.text("Advance:", sX, y);
    doc.text(`Rs. ${fmt(deal.advanceAmount)}`, sVX, y, { align: "right" });
    y += 6;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(38, 115, 85);
  doc.text("Total Received:", sX, y);
  doc.text(`Rs. ${fmt(deal.totalPaid)}`, sVX, y, { align: "right" });
  y += 6;

  doc.setFont("helvetica", "bold");
  doc.setTextColor(234, 88, 12);
  doc.text("Pending:", sX, y);
  doc.text(`Rs. ${fmt(deal.pendingAmount)}`, sVX, y, { align: "right" });
  y += 10;

  // --- Payment History ---
  const sorted = [...deal.payments].sort(
    (a, b) => new Date(a.paidAt).getTime() - new Date(b.paidAt).getTime()
  );

  if (sorted.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(5, 25, 18);
    doc.text("Payment History", m, y);
    y += 6;

    const payRows: string[][] = sorted.map((p: PaymentEntry, i: number) => [
      String(i + 1),
      fmtDateTime(p.paidAt),
      p.method,
      p.txnId || "-",
      `Rs. ${fmt(p.amount)}`,
      p.note || "-",
    ]);

    autoTable(doc, {
      startY: y,
      head: [["#", "Date & Time", "Method", "Bank", "Amount", "Note"]],
      body: payRows,
      margin: { left: m, right: m },
      theme: "grid",
      headStyles: {
        fillColor: [38, 115, 85],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 8,
        halign: "center",
      },
      bodyStyles: { fontSize: 8, textColor: [55, 71, 65], halign: "center" },
      columnStyles: {
        0: { halign: "center", cellWidth: 8 },
        1: { halign: "left", cellWidth: 38 },
        2: { halign: "center", cellWidth: 24 },
        3: { halign: "left", cellWidth: 30 },
        4: { halign: "right", cellWidth: 28 },
        5: { halign: "left", cellWidth: "auto" },
      },
      alternateRowStyles: { fillColor: [247, 249, 248] },
    });

    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // --- Payment Info ---
  if (tenant.paymentInfo) {
    doc.setDrawColor(230, 232, 231);
    doc.line(m, y, pw - m, y);
    y += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(5, 25, 18);
    doc.text("Payment Information", m, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(55, 71, 65);
    for (const line of tenant.paymentInfo.split("\n")) {
      doc.text(line, m, y);
      y += 4.5;
    }
  }

  // --- Footer ---
  const ph = doc.internal.pageSize.getHeight();
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(155, 163, 160);
  doc.text("powered by xale.in", pw / 2, ph - 10, { align: "center" });

  const dateTag = fmtDate(new Date().toISOString()).replace(/\s/g, "-");
  doc.save(
    `INVOICE_${docNumber}_${deal.dealName.replace(/\s+/g, "_")}_${dateTag}.pdf`
  );
}
